import React, { useState } from 'react';
import { Check, Circle, Lock, Loader2, ChevronRight, ScrollText, Package } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { copy } from '../../copy';
import { AuditLog } from './AuditLog';
import { ExportPackage } from './ExportPackage';
type StepStatus = 'complete' | 'active' | 'pending' | 'locked';
function StepIcon({
  status
}: {
  status: StepStatus;
}) {
  if (status === 'complete') return <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm" data-id="element-198">
        <Check size={15} strokeWidth={3} data-id="element-199" />
      </span>;
  if (status === 'active') return <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-white shadow-sm" data-id="element-200">
        <Loader2 size={15} className="animate-spin" strokeWidth={2.5} data-id="element-201" />
      </span>;
  if (status === 'locked') return <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#1a1a1a] text-slate-400 dark:text-[#555]" data-id="element-202">
        <Lock size={13} data-id="element-203" />
      </span>;
  return <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#1a1a1a] text-slate-300 dark:text-[#444]" data-id="element-204">
      <Circle size={11} data-id="element-205" />
    </span>;
}
const statusTextClasses: Record<StepStatus, string> = {
  complete: 'text-emerald-600 dark:text-emerald-400',
  active: 'text-blue-600 dark:text-blue-400',
  pending: 'text-slate-400 dark:text-[#737373]',
  locked: 'text-slate-400 dark:text-[#555]'
};
export function WorkflowPipeline() {
  const navigate = useNavigate();
  const [auditOpen, setAuditOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const steps = copy.workflow.steps;
  const labels = copy.workflow.statusLabels;
  return <>
      <section aria-label={copy.workflow.title} className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 space-y-4 hover:border-slate-300 dark:hover:border-[#444] transition-all" data-id="element-206">
        <div className="flex items-center justify-between" data-id="element-207">
          <h3 className="font-semibold text-slate-900 dark:text-white" data-id="element-208">
            {copy.workflow.title}
          </h3>
          <div className="flex items-center gap-1" data-id="element-209">
            <button onClick={() => setAuditOpen(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-500 dark:text-[#a3a3a3] hover:bg-slate-200 dark:hover:bg-white/5 active:scale-95 transition-all" title={copy.workflow.auditLogTitle} data-id="element-210">
              <ScrollText size={13} data-id="element-211" />
              {copy.workflow.auditLogTitle}
            </button>
          </div>
        </div>

        <ol className="relative space-y-1" data-id="element-212">
          {steps.map((step, i) => {
          const status = step.status as StepStatus;
          const isLast = i === steps.length - 1;
          const interactive = status !== 'locked' || step.id === 'sentencing';
          return <li key={step.id} className="relative" data-id="element-213">
                {!isLast && <span aria-hidden className={`absolute left-[13px] top-8 h-[calc(100%-16px)] w-0.5 rounded-full transition-colors ${status === 'complete' ? 'bg-emerald-400 dark:bg-emerald-600' : 'bg-slate-200 dark:bg-[#333]'}`} data-id="element-214" />}
                <button type="button" disabled={!interactive} onClick={() => interactive && navigate(step.route)} className={`group flex w-full items-start gap-3 rounded-xl p-2.5 text-left transition-all ${interactive ? 'hover:bg-slate-100 dark:hover:bg-white/5 active:scale-[0.99] cursor-pointer' : 'cursor-not-allowed opacity-75'}`} data-id="element-215">
                  <span className="relative z-10 mt-0.5 transition-transform group-hover:scale-105" data-id="element-216">
                    <StepIcon status={status} data-id="element-217" />
                  </span>
                  <span className="flex-1 min-w-0" data-id="element-218">
                    <span className="flex items-center justify-between gap-2" data-id="element-219">
                      <span className={`text-sm font-semibold transition-colors ${status === 'pending' || status === 'locked' ? 'text-slate-400 dark:text-[#737373]' : 'text-slate-900 dark:text-[#e5e5e5] group-hover:text-slate-900 dark:group-hover:text-white'}`} data-id="element-220">
                        {step.label}
                      </span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${statusTextClasses[status]}`} data-id="element-221">
                        {labels[status]}
                      </span>
                    </span>
                    <span className="mt-1 block text-[11px] text-slate-500 dark:text-[#737373] leading-relaxed" data-id="element-222">
                      {step.description}
                    </span>
                  </span>
                  {interactive && <ChevronRight size={15} className="mt-1.5 text-slate-300 dark:text-[#555] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" data-id="element-223" />}
                </button>
              </li>;
        })}
        </ol>

        <button onClick={() => setExportOpen(true)} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-500 hover:bg-blue-600 active:scale-[0.98] px-4 py-3 text-sm font-semibold text-white transition-all shadow-sm hover:shadow-md" data-id="element-224">
          <Package size={16} strokeWidth={2.5} data-id="element-225" />
          {copy.workflow.exportLabel}
        </button>
      </section>

      <AuditLog open={auditOpen} onClose={() => setAuditOpen(false)} data-id="element-226" />
      <ExportPackage open={exportOpen} onClose={() => setExportOpen(false)} data-id="element-227" />
    </>;
}